/* eslint-disable jsx-a11y/iframe-has-title */
/* eslint-disable react/jsx-sort-props */

import { graphql, useStaticQuery } from 'gatsby';

import { useEffect, useState, type FC, type ReactNode } from 'react';

import type { GlobalPageContext } from '../@types/app';

import PreFooter from '../components/pre-footer';

import RequestFormModal from '../components/Request-form/request-form-modal';
import FloatingActions from '../components/shared/floating-actions';

import Footer from './footer';

import LayoutHeader from './header';
import { FooterCtaProvider } from '../lib/footer-cta-context';
import { RequestFormProvider } from '../lib/request-form-context';

import AboutLeftSidebar from '../components/about/left-sidebar';
import IndustriesLeftSidebar from '../components/industries/left-sidebar';
import Breadcrumb from '../components/shared/breadcrum';
import { pathToAboutTabKey, normalizeAboutTabKey } from '../lib/about-routes';
import {
  INDUSTRY_BASE_PATH,
  industrySlugFromPath,
  isIndustriesWorkpiecePath,
} from '../lib/industry-routes';
import { globalHistory } from '@gatsbyjs/reach-router';



interface LayoutProps {
  children: ReactNode;
}



const Layout: FC<LayoutProps> = ({ children }) => {

  const [pathname, setPathname] = useState(() => {
    // On the server, there's no window; fall back to root.
    if (typeof window === 'undefined') return '/';
    return window.location.pathname;
  });

  useEffect(() => {
    // `globalHistory` is available at runtime and does not require
    // a LocationContext.Provider like `useLocation` does.
    return globalHistory.listen(({ location }) => {
      setPathname(location.pathname);
    });
  }, []);

  const isBlogDetailPage = pathname.startsWith('/blog/');
  const isBlogListingPage = pathname.startsWith('/blogs/');
  const isAboutPage =
    pathname === '/about' ||
    pathname === '/about/' ||
    pathname.startsWith('/about/');
  const isIndustriesWorkpiecePage = isIndustriesWorkpiecePath(pathname);
  const hideFloatingActions =
    pathname === '/contact-us/' ||
    pathname === '/contact-us' ||
    pathname === '/thank-you/' ||
    pathname === '/thank-you';

  const layoutData = useStaticQuery<{
    allStrapiBlogCategory: { nodes: { Title: string; Slug: string }[] };
    allStrapiBlog: {
      nodes: { blogCategory?: { Slug?: string | null } | null }[];
    };
    strapiAboutPage: {
      Tabs: {
        tabKey: string;
        label: string;
        position: number;
        isEnabled: boolean;
      }[];
    };
    strapiIndustryPage: {
      PageTitle: string;
    };
    allStrapiIndustry: {
      nodes: {
        Slug: string;
        Title: string;
        position?: number | null;
      }[];
    };
  }>(graphql`
    query LayoutShellQuery {
      allStrapiBlogCategory(sort: { position: ASC }) {
        nodes {
          Title
          Slug
        }
      }
      allStrapiBlog {
        nodes {
          blogCategory {
            Slug
          }
        }
      }
      strapiAboutPage {
        Tabs {
          tabKey
          label
          position
          isEnabled
        }
      }
      strapiIndustryPage {
        PageTitle
      }
      allStrapiIndustry(sort: { position: ASC }) {
        nodes {
          Slug
          Title
          position
        }
      }
    }
  `);

  // Sidebar: only categories that currently have posts.
  // Keep "All News" when any blogs exist (it routes to the full listing).
  const blogCategories = (() => {
    const allCategories = layoutData?.allStrapiBlogCategory?.nodes ?? [];
    const blogNodes = layoutData?.allStrapiBlog?.nodes ?? [];
    const slugsWithPosts = new Set(
      blogNodes
        .map((blog) => blog.blogCategory?.Slug)
        .filter((slug): slug is string => Boolean(slug)),
    );
    const hasAnyBlogs = blogNodes.length > 0;

    return allCategories.filter((category) => {
      if (category.Slug === 'all-news') return hasAnyBlogs;
      return slugsWithPosts.has(category.Slug);
    });
  })();
  const aboutTabs = layoutData?.strapiAboutPage?.Tabs ?? [];
  const industryNavItems = layoutData?.allStrapiIndustry?.nodes ?? [];
  const industryPageTitle =
    layoutData?.strapiIndustryPage?.PageTitle?.trim() ||
    'Industries we cater to';
  const activeAboutTabKey = pathToAboutTabKey(pathname);
  const activeIndustrySlug = industrySlugFromPath(pathname);

  const aboutBreadcrumbItems = (() => {
    const items = [
      { label: 'HOME', link: '/' },
      { label: 'ABOUT US', link: '/about/' },
    ];

    const activeTab = aboutTabs.find(
      (tab) => normalizeAboutTabKey(tab.tabKey) === activeAboutTabKey,
    );
    if (activeTab && normalizeAboutTabKey(activeTab.tabKey) !== 'our-vision') {
      items.push({
        label: activeTab.label.toUpperCase(),
        link: pathname,
      });
    }

    return items;
  })();

  const industriesBreadcrumbItems = (() => {
    const items = [
      { label: 'HOME', link: '/' },
      { label: 'INDUSTRY', link: `${INDUSTRY_BASE_PATH}/` },
      {
        label: industryPageTitle.toUpperCase(),
        link: `${INDUSTRY_BASE_PATH}/`,
      },
    ];

    const activeIndustry = industryNavItems.find(
      (industry) => industry.Slug === activeIndustrySlug,
    );
    if (activeIndustry) {
      items.push({
        label: activeIndustry.Title.toUpperCase(),
        link: pathname,
      });
    }

    return items;
  })();

  const activeBlogCategorySlug = (() => {
    // Routes:
    // - /blogs/:page/               => "all-news"
    // - /blogs/:categorySlug/:page/ => category slug
    const match = pathname.match(/^\/blogs\/([^/]+)\/?/);
    const firstSegment = match?.[1];
    if (!firstSegment) return null;
    if (/^\d+$/.test(firstSegment)) return null;
    return firstSegment;
  })();

  const blogBreadcrumbItems = (() => {
    const items = [
      { label: 'HOME', link: '/' },
      { label: 'BLOGS', link: '/blogs/1/' },
    ];

    if (activeBlogCategorySlug) {
      const category = blogCategories.find(
        (c) => c.Slug === activeBlogCategorySlug,
      );
      if (category) {
        items.push({
          label: category.Title.toUpperCase(),
          link: `/blogs/${category.Slug}/1/`,
        });
      }
    }

    return items;
  })();

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);



  const openRequestForm = () => {

    setShowRequestForm(true);

  };



  return (
    <FooterCtaProvider>
    <RequestFormProvider>
    <div

      className={`${!isBlogDetailPage ? 'overflow-x-clip' : ''} max-w-[3100px] mx-auto shadow-2xl`}

    >

      {/* Google Tag Manager (noscript) */}

      <noscript>

        <iframe

          src="https://www.googletagmanager.com/ns.html?id=GTM-58ZV457B"

          height="0"

          width="0"

          style={{ display: 'none', visibility: 'hidden' }}

        />

      </noscript>

      {/* End Google Tag Manager (noscript) */}

      <LayoutHeader onOpenRequestForm={openRequestForm} />



      <main className="site-main">
        {isBlogListingPage ? (
          <>
            <div className="blog-listing-breadcrumb-wrap">
              <Breadcrumb
                className="blog-listing-breadcrumb"
                items={blogBreadcrumbItems}
              />
            </div>
            <div
              aria-hidden="true"
              className="blog-listing-scroll-anchor"
              id="blog-listing-scroll-anchor"
            />
            <div className="container mx-auto px-4 py-8 sm:px-6 md:py-14">
              <div className="site-split-layout__main relative z-0 min-w-0 overflow-visible">
                {children}
              </div>
            </div>
          </>
        ) : isAboutPage ? (
          <>
            <div className="blog-listing-breadcrumb-wrap">
              <Breadcrumb
                className="blog-listing-breadcrumb"
                items={aboutBreadcrumbItems}
              />
            </div>
            <div
              aria-hidden="true"
              className="blog-listing-scroll-anchor"
              id="about-page-scroll-anchor"
            />
            <div className="container mx-auto px-4 py-8 sm:px-6 md:py-14">
              <div className="site-split-layout grid grid-cols-1 items-start gap-8 md:grid-cols-12 md:gap-6">
                <AboutLeftSidebar
                  activeTabKey={activeAboutTabKey}
                  tabs={aboutTabs}
                />
                <div className="site-split-layout__main relative z-0 min-w-0 overflow-visible md:col-span-9">{children}</div>
              </div>
            </div>
          </>
        ) : isIndustriesWorkpiecePage ? (
          <>
            <div className="blog-listing-breadcrumb-wrap">
              <Breadcrumb
                className="blog-listing-breadcrumb"
                items={industriesBreadcrumbItems}
              />
            </div>
            <div
              aria-hidden="true"
              className="blog-listing-scroll-anchor"
              id="industries-page-scroll-anchor"
            />
            <div className="container mx-auto px-4 py-8 sm:px-6 md:py-14">
              <div className="site-split-layout grid grid-cols-1 items-start gap-8 md:grid-cols-12 md:gap-6">
                <IndustriesLeftSidebar
                  activeSlug={activeIndustrySlug}
                  industries={industryNavItems}
                />
                <div className="site-split-layout__main relative z-0 min-w-0 overflow-visible md:col-span-9">{children}</div>
              </div>
            </div>
          </>
        ) : (
          children
        )}
      </main>



      <PreFooter onInquire={openRequestForm} />



      <Footer />



      <RequestFormModal setShow={setShowRequestForm} show={showRequestForm} />

      {isClient && !hideFloatingActions ? (
        <FloatingActions onOpenEnquiry={openRequestForm} />
      ) : null}

    </div>
    </RequestFormProvider>
    </FooterCtaProvider>
  );

};



export default Layout;

